import { Autorenew as AutorenewIcon, ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { MarkdownBlock } from "../../../../components/markdown-block";
import type { Test } from "../../../../types";

interface IntentTabProps {
  test: Test | undefined;
  onCopyIntent: (intent: string) => void;
  onRegenerateDescription?: () => void;
  isRegenerating?: boolean;
}

export function IntentTab({
  test,
  onCopyIntent,
  onRegenerateDescription,
  isRegenerating = false,
}: IntentTabProps) {
  if (!test?.aaa_description) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "300px",
          textAlign: "center",
          p: 4,
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          No Test Intent Available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "400px" }}>
          This test doesn't have an AAA description yet. The intent will appear here once it's
          generated or added to the test.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 0,
        maxHeight: "calc(100vh - 200px)",
        overflow: "auto",
        backgroundColor: "background.paper",
        position: "relative",
        borderRadius: 1,
        border: "none",
      }}
    >
      {/* Buttons positioned at top right */}
      <Box
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 1,
          display: "flex",
          gap: 1,
        }}
      >
        {onRegenerateDescription && (
          <IconButton
            title="Regenerate description"
            onClick={onRegenerateDescription}
            disabled={isRegenerating}
            sx={{
              px: 1,
              py: 1,
              textTransform: "none",
              fontWeight: 300,
              fontSize: "0.675rem",
              borderColor: "text.secondary",
              color: "text.secondary",
              backgroundColor: "background.paper",
              boxShadow: "none",
              borderWidth: 0,
              "&:hover": {
                color: "grey.700",
                backgroundColor: "background.default",
              },
              "&:disabled": {
                color: "text.disabled",
              },
            }}
          >
            {isRegenerating ? <CircularProgress size={20} /> : <AutorenewIcon />}
          </IconButton>
        )}
        <IconButton
          title="Use as template for new test"
          onClick={() => onCopyIntent(test.aaa_description || "")}
          sx={{
            px: 1,
            py: 1,
            textTransform: "none",
            fontWeight: 300,
            fontSize: "0.675rem",
            borderColor: "text.secondary",
            color: "text.secondary",
            backgroundColor: "background.paper",
            boxShadow: "none",
            borderWidth: 0,
            "&:hover": {
              color: "grey.700",
              backgroundColor: "background.default",
            },
          }}
        >
          <ContentCopyIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          pl: 2,
          pr: 2,
          minHeight: "calc(50vh)", // Account for padding
        }}
      >
        <MarkdownBlock content={test.aaa_description} />
      </Box>
    </Box>
  );
}
